import { buildApiUrl } from '../utils/apiSetup';
const authHeaders = (json = true) => {
  const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
};

const parseError = async (response, fallback) => {
  try {
    const data = await response.json();
    if (typeof data.detail === 'string') return { message: data.detail, status: response.status };
  } catch {
    /* ignore */
  }
  return { message: fallback, status: response.status };
};

export async function fetchTaxConfigurations(signal) {
  const response = await fetch(buildApiUrl('/api/v1/income-tax/configurations'), { headers: authHeaders(false), signal });
  if (!response.ok) {
    const error = await parseError(response, 'Failed to load tax configurations');
    throw new Error(error.message);
  }
  return response.json();
}

export async function saveTaxConfiguration(config, signal) {
  const { id, ...payload } = config;
  const method = id ? 'PUT' : 'POST';
  const endpoint = id ? `/api/v1/income-tax/configurations/${id}` : '/api/v1/income-tax/configurations';
  const response = await fetch(buildApiUrl(endpoint), {
    method,
    headers: authHeaders(),
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    const error = await parseError(response, 'Failed to save tax configuration');
    throw new Error(error.message);
  }
  return response.json();
}

export async function fetchEmployeeTaxProfile(employeeId, taxYear, signal) {
  const response = await fetch(buildApiUrl(`/api/v1/income-tax/employees/${employeeId}/tax-profile?tax_year=${taxYear}`), {
    headers: authHeaders(false),
    signal
  });
  if (!response.ok) {
    const error = await parseError(response, 'Failed to load employee tax profile');
    throw new Error(error.message);
  }
  return response.json();
}

export async function updateEmployeeTaxProfile(employeeId, taxYear, regime, signal) {
  const response = await fetch(buildApiUrl(`/api/v1/income-tax/employees/${employeeId}/tax-profile?tax_year=${taxYear}`), {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ regime }),
    signal
  });
  if (!response.ok) {
    const error = await parseError(response, 'Failed to update employee tax profile');
    throw new Error(error.message);
  }
  return response.json();
}

export async function fetchEmployeeTaxSummary(employeeId, taxYear, signal) {
  const response = await fetch(buildApiUrl(`/api/v1/income-tax/employees/${employeeId}/tax-summary?tax_year=${taxYear}`), {
    headers: authHeaders(false),
    signal
  });
  if (!response.ok) {
    const error = await parseError(response, 'Failed to load tax summary');
    throw new Error(error.message);
  }
  return response.json();
}

export async function fetchTdsHistory(employeeId, taxYear, signal) {
  const response = await fetch(buildApiUrl(`/api/v1/income-tax/employees/${employeeId}/tds-history?tax_year=${taxYear}`), {
    headers: authHeaders(false),
    signal
  });
  if (!response.ok) {
    const error = await parseError(response, 'Failed to load TDS history');
    throw new Error(error.message);
  }
  return response.json();
}

export async function fetchEmployeeRegimes(taxYear, filters = {}, signal) {
  const params = new URLSearchParams({ tax_year: taxYear });
  if (filters.search) params.append('search', filters.search);
  if (filters.department_id) params.append('department_id', filters.department_id);
  if (filters.designation_id) params.append('designation_id', filters.designation_id);
  if (filters.regime) params.append('regime', filters.regime);
  if (filters.status) params.append('status', filters.status);
  if (filters.skip) params.append('skip', filters.skip);
  if (filters.limit) params.append('limit', filters.limit);

  const response = await fetch(buildApiUrl(`/api/v1/income-tax/employee-regimes?${params.toString()}`), {
    headers: authHeaders(false),
    signal
  });
  if (!response.ok) {
    const error = await parseError(response, 'Failed to load employee regimes');
    throw new Error(error.message);
  }
  return response.json();
}

export async function bulkAssignRegime(employeeIds, regime, taxYear, effectiveFrom = null, signal) {
  const payload = {
    employee_ids: employeeIds,
    regime,
    tax_year: taxYear
  };
  if (effectiveFrom) payload.effective_from = effectiveFrom;

  const response = await fetch(buildApiUrl('/api/v1/income-tax/employee-regimes/bulk-assign'), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
    signal
  });
  if (!response.ok) {
    const error = await parseError(response, 'Failed to bulk assign regime');
    throw new Error(error.message);
  }
  return response.json();
}
